import { useState } from "react";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Download,
  RefreshCw,
  MapPin,
  ExternalLink,
  Check,
  X,
  Merge,
  Plus,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import {
  usePlaceCandidates,
  usePlaceIngestRuns,
  useRunPlacesIngest,
  useRunPlacesMatch,
  useCreatePlaceFromCandidate,
  useMergeCandidateToPlace,
  useRejectCandidate,
  useCandidateCounts,
  type PlaceCandidate,
  type IngestRequest,
} from "@/hooks/usePlacesIngest";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import { buildDescriptionShort } from "@/lib/places/buildDescriptionShort";

// ============================================================
// PRESET AREAS (for quick testing)
// ============================================================

const PRESET_AREAS = [
  {
    label: "Dalarna (test)",
    center: [14.5, 60.6] as [number, number],
    radius_m: 30000,
  },
  {
    label: "Gotland",
    center: [18.5, 57.6] as [number, number],
    radius_m: 50000,
  },
  {
    label: "Skåne (Österlen)",
    center: [14.1, 55.7] as [number, number],
    radius_m: 30000,
  },
];

// ============================================================
// INGEST FORM
// ============================================================

interface IngestFormValues {
  mode: "nearby" | "bbox";
  preset: string;
  centerLng: string;
  centerLat: string;
  radius_m: string;
  limit: string;
}

function IngestForm() {
  const [selectedTags, setSelectedTags] = useState<string[]>(["farm", "cafe", "lodging"]);
  const { register, handleSubmit, watch, setValue } = useForm<IngestFormValues>({
    defaultValues: {
      mode: "nearby",
      preset: "",
      centerLng: "14.5",
      centerLat: "60.6",
      radius_m: "30000",
      limit: "50",
    },
  });

  const runIngest = useRunPlacesIngest();
  const mode = watch("mode");

  const handlePresetChange = (preset: string) => {
    setValue("preset", preset);
    const area = PRESET_AREAS.find((a) => a.label === preset);
    if (area) {
      setValue("centerLng", String(area.center[0]));
      setValue("centerLat", String(area.center[1]));
      setValue("radius_m", String(area.radius_m));
    }
  };

  const onSubmit = (data: IngestFormValues) => {
    const request: IngestRequest = {
      source: "osm",
      mode: data.mode,
      limit: parseInt(data.limit, 10),
      tags: selectedTags,
    };

    if (data.mode === "nearby") {
      request.center = [parseFloat(data.centerLng), parseFloat(data.centerLat)];
      request.radius_m = parseInt(data.radius_m, 10);
    }

    runIngest.mutate(request);
  };

  const availableTags = ["farm", "cafe", "restaurant", "lodging", "shop", "winery", "brewery", "market"];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          Kör import (OSM)
        </CardTitle>
        <CardDescription>
          Hämta platser från OpenStreetMap via Overpass API
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Snabbval område</Label>
              <Select onValueChange={handlePresetChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Välj ett område..." />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  {PRESET_AREAS.map((area) => (
                    <SelectItem key={area.label} value={area.label}>
                      {area.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Läge</Label>
              <Select
                value={mode}
                onValueChange={(v) => setValue("mode", v as "nearby" | "bbox")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  <SelectItem value="nearby">Radie från punkt</SelectItem>
                  <SelectItem value="bbox">Bounding box</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {mode === "nearby" && (
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Longitud</Label>
                <Input {...register("centerLng")} type="number" step="0.001" />
              </div>
              <div className="space-y-2">
                <Label>Latitud</Label>
                <Input {...register("centerLat")} type="number" step="0.001" />
              </div>
              <div className="space-y-2">
                <Label>Radie (meter)</Label>
                <Input {...register("radius_m")} type="number" />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Taggar att söka</Label>
            <div className="flex flex-wrap gap-2">
              {availableTags.map((tag) => (
                <Badge
                  key={tag}
                  variant={selectedTags.includes(tag) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => {
                    setSelectedTags((prev) =>
                      prev.includes(tag)
                        ? prev.filter((t) => t !== tag)
                        : [...prev, tag]
                    );
                  }}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Max antal</Label>
            <Input {...register("limit")} type="number" className="w-32" />
          </div>

          <Button type="submit" disabled={runIngest.isPending}>
            {runIngest.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Kör import
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ============================================================
// MATCH BATCH SECTION
// ============================================================

function MatchBatchSection() {
  const runMatch = useRunPlacesMatch();
  const { data: counts, isLoading } = useCandidateCounts();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Matcha kandidater
        </CardTitle>
        <CardDescription>
          Jämför nya kandidater mot befintliga platser
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground">
            {isLoading ? (
              <Skeleton className="h-4 w-32" />
            ) : (
              <>
                <span className="font-medium">{counts?.new ?? 0}</span> nya kandidater att matcha
              </>
            )}
          </div>
        </div>

        <Button
          onClick={() => runMatch.mutate(50)}
          disabled={runMatch.isPending || (counts?.new ?? 0) === 0}
        >
          {runMatch.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Kör matchning (50 st)
        </Button>
      </CardContent>
    </Card>
  );
}

// ============================================================
// INGEST HISTORY
// ============================================================

function IngestHistory() {
  const { data: runs, isLoading } = usePlaceIngestRuns(10);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (!runs?.length) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Inga importkörningar än.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Källa</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Nya</TableHead>
          <TableHead>Uppdaterade</TableHead>
          <TableHead>Tid</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.map((run) => (
          <TableRow key={run.id}>
            <TableCell className="font-medium">{run.source.toUpperCase()}</TableCell>
            <TableCell>
              {run.status === "running" && (
                <Badge variant="secondary">
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Pågår
                </Badge>
              )}
              {run.status === "success" && (
                <Badge variant="default">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Klar
                </Badge>
              )}
              {run.status === "failed" && (
                <Badge variant="destructive">
                  <XCircle className="h-3 w-3 mr-1" />
                  Misslyckad
                </Badge>
              )}
            </TableCell>
            <TableCell>{run.inserted_count}</TableCell>
            <TableCell>{run.updated_count}</TableCell>
            <TableCell className="text-muted-foreground text-sm">
              {formatDistanceToNow(new Date(run.started_at), {
                addSuffix: true,
                locale: sv,
              })}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ============================================================
// CANDIDATE ROW
// ============================================================

interface CandidateRowProps {
  candidate: PlaceCandidate;
  onAction: (action: "create" | "merge" | "reject", candidate: PlaceCandidate) => void;
}

function CandidateRow({ candidate, onAction }: CandidateRowProps) {
  const confidenceColor =
    candidate.confidence >= 0.85
      ? "text-green-600"
      : candidate.confidence >= 0.6
      ? "text-yellow-600"
      : "text-red-600";

  const thumbSrc = candidate.image_thumb_url || candidate.image_url;

  return (
    <TableRow>
      <TableCell>
        <div className="flex gap-3 items-start">
          {thumbSrc ? (
            <img
              src={thumbSrc}
              alt=""
              className="w-12 h-12 rounded object-cover flex-shrink-0 bg-muted"
              loading="lazy"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          ) : (
            <div className="w-12 h-12 rounded bg-muted flex items-center justify-center flex-shrink-0">
              <MapPin className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          <div className="space-y-1 min-w-0">
            <div className="font-medium">{candidate.name}</div>
            <div className="text-sm text-muted-foreground truncate">
              {candidate.address_text || candidate.municipality || "Ingen adress"}
            </div>
            {candidate.tags.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                {candidate.tags.slice(0, 3).map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline">{candidate.source.toUpperCase()}</Badge>
      </TableCell>
      <TableCell>
        <span className={confidenceColor}>{(candidate.confidence * 100).toFixed(0)}%</span>
      </TableCell>
      <TableCell>
        {candidate.matched_place ? (
          <div className="text-sm">
            <span className="font-medium">{candidate.matched_place.name}</span>
            <a
              href={`/app/admin/places/${candidate.matched_place.id}/edit`}
              className="text-primary hover:underline ml-2"
            >
              Visa →
            </a>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          {candidate.source_url && (
            <Button
              variant="ghost"
              size="icon"
              asChild
              title="Öppna i OSM"
            >
              <a href={candidate.source_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          )}
          {candidate.lat && candidate.lng && (
            <Button
              variant="ghost"
              size="icon"
              asChild
              title="Öppna i Google Maps"
            >
              <a
                href={`https://www.google.com/maps?q=${candidate.lat},${candidate.lng}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MapPin className="h-4 w-4" />
              </a>
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAction("create", candidate)}
            title="Skapa ny plats"
          >
            <Plus className="h-4 w-4" />
          </Button>
          {candidate.matched_place && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAction("merge", candidate)}
              title="Merga till matchad plats"
            >
              <Merge className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAction("reject", candidate)}
            title="Avvisa"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// ============================================================
// CANDIDATES QUEUE
// ============================================================

function CandidatesQueue() {
  const [activeTab, setActiveTab] = useState<"new" | "needs_review" | "matched">("new");
  const { data: candidates, isLoading } = usePlaceCandidates(activeTab);
  const { data: counts } = useCandidateCounts();

  const [actionDialog, setActionDialog] = useState<{
    action: "create" | "merge" | "reject";
    candidate: PlaceCandidate;
  } | null>(null);
  const [notes, setNotes] = useState("");
  const [descriptionShort, setDescriptionShort] = useState("");

  const createPlace = useCreatePlaceFromCandidate();
  const mergePlace = useMergeCandidateToPlace();
  const rejectCandidate = useRejectCandidate();

  const handleAction = (action: "create" | "merge" | "reject", candidate: PlaceCandidate) => {
    setNotes("");
    if (action === "create") {
      // Pre-fill with auto-generated description
      setDescriptionShort(
        buildDescriptionShort({
          name: candidate.name,
          tags: candidate.tags,
          municipality: candidate.municipality,
          county: candidate.county,
          landskap: candidate.landskap,
        })
      );
    } else {
      setDescriptionShort("");
    }
    setActionDialog({ action, candidate });
  };

  const confirmAction = () => {
    if (!actionDialog) return;

    const { action, candidate } = actionDialog;

    if (action === "create") {
      createPlace.mutate({ candidateId: candidate.id, notes, descriptionShort });
    } else if (action === "merge" && candidate.matched_place_id) {
      mergePlace.mutate({
        candidateId: candidate.id,
        placeId: candidate.matched_place_id,
        notes,
      });
    } else if (action === "reject") {
      rejectCandidate.mutate({ candidateId: candidate.id, notes });
    }

    setActionDialog(null);
  };

  const isPending = createPlace.isPending || mergePlace.isPending || rejectCandidate.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Platskandidater</CardTitle>
        <CardDescription>
          Granska och godkänn importerade platser
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
          <TabsList>
            <TabsTrigger value="new" className="gap-2">
              <Clock className="h-4 w-4" />
              Nya
              {counts?.new ? <Badge variant="secondary">{counts.new}</Badge> : null}
            </TabsTrigger>
            <TabsTrigger value="needs_review" className="gap-2">
              <AlertCircle className="h-4 w-4" />
              Behöver granskning
              {counts?.needs_review ? <Badge variant="secondary">{counts.needs_review}</Badge> : null}
            </TabsTrigger>
            <TabsTrigger value="matched" className="gap-2">
              <Check className="h-4 w-4" />
              Matchade
              {counts?.matched ? <Badge variant="secondary">{counts.matched}</Badge> : null}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : !candidates?.length ? (
              <div className="text-center py-8 text-muted-foreground">
                Inga kandidater i denna kö
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plats</TableHead>
                    <TableHead>Källa</TableHead>
                    <TableHead>Konfidens</TableHead>
                    <TableHead>Matchad plats</TableHead>
                    <TableHead>Länkar</TableHead>
                    <TableHead>Åtgärder</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidates.map((candidate) => (
                    <CandidateRow
                      key={candidate.id}
                      candidate={candidate}
                      onAction={handleAction}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>
        </Tabs>

        {/* Action confirmation dialog */}
        <AlertDialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {actionDialog?.action === "create" && "Skapa ny plats?"}
                {actionDialog?.action === "merge" && "Merga till befintlig plats?"}
                {actionDialog?.action === "reject" && "Avvisa kandidat?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {actionDialog?.action === "create" && (
                  <>
                    En ny plats med namn "{actionDialog.candidate.name}" kommer att skapas som <strong>utkast</strong>.
                    Du behöver publicera den manuellt.
                  </>
                )}
                {actionDialog?.action === "merge" && (
                  <>
                    Kandidaten kommer att mergas till "{actionDialog.candidate.matched_place?.name}".
                    Saknade fält (telefon, webbplats, etc.) kommer att uppdateras.
                  </>
                )}
                {actionDialog?.action === "reject" && (
                  <>
                    Kandidaten "{actionDialog.candidate.name}" kommer att avvisas och syns inte mer i kön.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>

            {actionDialog?.action === "create" && (
              <div className="space-y-2">
                <Label>Kort beskrivning</Label>
                <Textarea
                  value={descriptionShort}
                  onChange={(e) => setDescriptionShort(e.target.value)}
                  placeholder="Visas i listor och i toppsektionen."
                  rows={3}
                  maxLength={200}
                />
                <p className="text-xs text-muted-foreground">
                  {descriptionShort.length}/160 tecken – visas i listor och toppsektion
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Anteckning (valfritt)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Lägg till en anteckning..."
                rows={2}
              />
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel>Avbryt</AlertDialogCancel>
              <AlertDialogAction onClick={confirmAction} disabled={isPending}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {actionDialog?.action === "create" && "Skapa plats"}
                {actionDialog?.action === "merge" && "Merga"}
                {actionDialog?.action === "reject" && "Avvisa"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

// ============================================================
// MAIN PAGE
// ============================================================

export default function AdminPlacesIngestPage() {
  return (
    <AdminLayout title="Platsimport">
      <div className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-2">
          <IngestForm />
          <MatchBatchSection />
        </div>

        <Separator />

        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Importhistorik</h3>
          <IngestHistory />
        </div>

        <Separator />

        <CandidatesQueue />
      </div>
    </AdminLayout>
  );
}
